import { Experience } from "types/experience";
import { TimeSlot } from "types/time-slot";

interface ExperienceChains {
    startTimes: TimeSlot[];
    chains: TimeSlot[][];
}

/**
 * Finds valid chains for an Experience.
 *
 * A valid chain is a sequence of time slots — one per activity — where each subsequent slot
 * starts within `maximumGapTime` minutes of the previous one ending, and every slot has
 * availability for the requested party size. The gap is the first time set's, falling back to the
 * experience's.
 *
 * For each first-activity start slot, the search short-circuits as soon as one complete
 * valid chain is found; at most one chain is returned per qualifying start slot.
 *
 * @param experience - The experience to resolve chains for
 * @param guests - Party size to validate availability against
 * @returns At most one valid chain for each qualifying first-activity time slot, and the
 * corresponding start time slots
 */

function findExperienceChains(
    experience: Experience,
    guests: number | undefined,
    isBookingRequest = false
): ExperienceChains {
    const activities = experience.activities;

    if (!activities || activities.length === 0 || (!guests && !isBookingRequest)) {
        return { startTimes: [], chains: [] };
    }

    const firstActivitySlots = activities[0].timeSets.flatMap((timeSet) =>
        timeSet.times
            .filter((slot) => isBookingRequest || hasAvailabilityForGuests(slot, guests!))
            .map((slot) => ({
                slot,
                maximumGapTime: timeSet.maximumGapTime ?? experience.maximumGapTime,
            }))
    );

    const startTimes: TimeSlot[] = [];
    const chains: TimeSlot[][] = [];

    for (const { slot, maximumGapTime } of firstActivitySlots) {
        const chain = findFirstChain(
            slot,
            activities,
            0,
            maximumGapTime,
            guests ?? 0,
            isBookingRequest
        );
        if (chain) {
            startTimes.push(slot);
            chains.push(chain);
        }
    }

    return { startTimes, chains };
}

/**
 * Returns the first valid complete chain starting from `slot` at `activityIndex`, or null
 * if no valid chain exists. Short-circuits as soon as one complete path is found.
 */
function findFirstChain(
    slot: TimeSlot,
    activities: Experience["activities"],
    activityIndex: number,
    maximumGapTime: number,
    guests: number,
    isBookingRequest: boolean
): TimeSlot[] | null {
    if (activityIndex === activities.length - 1) {
        return [slot];
    }

    const nextSlots = activities[activityIndex + 1].timeSets
        .flatMap((ts) => ts.times)
        .filter((ts) => isBookingRequest || hasAvailabilityForGuests(ts, guests));

    for (const next of findCompatibleSlots(nextSlots, slot.end, maximumGapTime)) {
        const rest = findFirstChain(
            next,
            activities,
            activityIndex + 1,
            maximumGapTime,
            guests,
            isBookingRequest
        );
        if (rest) return [slot, ...rest];
    }

    return null;
}

function findCompatibleSlots(
    timeSlots: TimeSlot[],
    endTime: IMoment,
    maximumGapTime: number
): TimeSlot[] {
    return timeSlots.filter((slot) => {
        const gap = slot.start.diff(endTime, "minutes");
        return gap >= 0 && gap <= maximumGapTime;
    });
}

function hasAvailabilityForGuests(timeSlot: TimeSlot, guests: number): boolean {
    return timeSlot.availableSeats?.includes(guests) ?? false;
}

export { findExperienceChains };
